import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import {
  DeductionChip,
  PriceRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { getCurrency } from "@/utils/translationHelpers";

export type DueNowProps = {
  totalOnce: number;
  subtotalOnce: number;
  discountAmount?: number;
  /** Named basket discount (e.g. "B2B 10%"); falls back to generic "Rabatt". */
  discountName?: string;
  discountEndDate?: Date;
  currency: string;
  dataRateName?: string;
  dataRatePrice?: number;
  billingPeriod: string;
  recurringCaption?: string;
  recurringAmount?: number;
  recurringDate?: string;
  recurringBillingPeriod?: string;
  voiceOptionName?: string | ReactNode;
  voiceOptionPrice?: number;
  watchSimName?: string;
  watchSimPrice?: number;
  credit?: number;
  creditTimeRemaining?: number;
  icon?: ReactNode;
  isPrecharged?: boolean;
  hideSubtotal?: boolean;
};

// TODO - Refactor the RoamingDueNow DeviceTile so they make more sense,
// and the DueNowLightInterface should be reused and renamed
export const DueNow = ({
  totalOnce,
  subtotalOnce,
  discountAmount,
  discountName,
  discountEndDate,
  currency,
  isPrecharged,
  billingPeriod,
  recurringCaption,
  recurringAmount,
  recurringDate,
  recurringBillingPeriod,
  dataRateName,
  dataRatePrice,
  voiceOptionName,
  voiceOptionPrice,
  watchSimName,
  watchSimPrice,
  credit,
  creditTimeRemaining,
  icon,
  hideSubtotal,
}: DueNowProps) => {
  const { t } = useTranslation();

  const hasCredit = !!credit && credit > 0;
  const hasCreditTimeRemaining =
    !!creditTimeRemaining && creditTimeRemaining > 0;
  const hasDiscount = !!discountAmount && discountAmount > 0;

  const shouldShowSubtotalSection = hideSubtotal
    ? hasCredit || hasCreditTimeRemaining || hasDiscount
    : !!subtotalOnce &&
      subtotalOnce > 0 &&
      (hasCredit ||
        hasCreditTimeRemaining ||
        hasDiscount ||
        subtotalOnce > totalOnce) &&
      (!isPrecharged || hasCredit || hasCreditTimeRemaining || hasDiscount);

  const showVoiceOption =
    !dataRateName?.startsWith("Flat Mobile") &&
    !!voiceOptionName &&
    voiceOptionPrice !== undefined &&
    voiceOptionPrice >= 0;

  const resolvedDiscountName =
    discountName ?? t("Onboarding.sections.checkout.summary.discount");

  return (
    <ProfileCard
      variant="white"
      className="self-start"
      title={
        <Trans components={[<span key="dueNow" />]} t={t}>
          Onboarding.sections.checkout.summary.payOnce
        </Trans>
      }
      actions={icon ? <div id="device-icon">{icon}</div> : undefined}
    >
      <div className="flex w-full flex-col">
        {!!dataRateName &&
          dataRatePrice !== undefined &&
          dataRatePrice >= 0 && (
            <PriceRow
              withBackdrop
              variant="strong"
              label={dataRateName}
              value={
                <span data-testid="flat-price">
                  {getCurrency(dataRatePrice, currency)}
                </span>
              }
            />
          )}

        {showVoiceOption && (
          <PriceRow
            label={
              <>
                {voiceOptionName}
                <br />
                <span className="text-body-xs font-regular leading-md">
                  {billingPeriod}
                </span>
              </>
            }
            value={
              <span data-testid="voice-price">
                {getCurrency(voiceOptionPrice, currency)}
              </span>
            }
          />
        )}

        {!!watchSimName &&
          watchSimPrice !== undefined &&
          watchSimPrice >= 0 && (
            <PriceRow
              label={
                <>
                  {watchSimName}
                  <br />
                  <span className="text-body-xs font-regular leading-md">
                    {billingPeriod}
                  </span>
                </>
              }
              value={
                <span data-testid="watch-price">
                  {getCurrency(watchSimPrice, currency)}
                </span>
              }
            />
          )}

        {shouldShowSubtotalSection && (
          <>
            {!hideSubtotal && (
              <PriceRow
                label={t("Onboarding.sections.checkout.summary.subtotal")}
                value={getCurrency(subtotalOnce, currency)}
              />
            )}

            {hasDiscount && (
              <PriceRow
                label={
                  <>
                    {resolvedDiscountName}
                    {discountEndDate && (
                      <Trans
                        t={t}
                        i18nKey="Onboarding.sections.checkout.summary.until_date"
                        values={{
                          date: discountEndDate.toLocaleDateString("de-CH"),
                        }}
                        components={[
                          <span
                            key="until_date"
                            className="text-foreground-error"
                          />,
                        ]}
                      />
                    )}
                  </>
                }
                value={<DeductionChip amount={discountAmount} />}
              />
            )}

            {hasCreditTimeRemaining && (
              <PriceRow
                withDivider
                label={
                  <>
                    {t("Onboarding.sections.checkout.summary.credit")}{" "}
                    <span className="text-body-xs font-regular leading-md">
                      {t("cockpit.managementTile.tariff.checkout.creditText")}
                    </span>
                  </>
                }
                value={<DeductionChip amount={creditTimeRemaining} />}
              />
            )}

            {hasCredit && (
              <PriceRow
                withDivider
                label={
                  <>
                    {t("Onboarding.sections.checkout.summary.credit")}{" "}
                    <span className="text-body-xs font-regular leading-md">
                      {t(
                        "cockpit.managementTile.tariff.checkout.creditTextOthers",
                      )}
                    </span>
                  </>
                }
                value={<DeductionChip amount={credit} />}
              />
            )}
          </>
        )}

        <PriceRow
          withTopDivider
          variant="strong"
          label={
            <>
              <span>{t("Onboarding.sections.checkout.summary.total")}</span>
              <br />
              <span className="text-body-xs font-regular leading-md">
                {t("Onboarding.sections.checkout.summary.taxIncluded")}
              </span>
            </>
          }
          value={
            <>
              <span data-testid="total-amount-due-now">
                {totalOnce <= 0
                  ? `${currency} 0.00`
                  : getCurrency(totalOnce, currency)}
              </span>
              {recurringCaption && (
                <span className="text-body-xs font-regular leading-md">
                  {recurringCaption}
                </span>
              )}
            </>
          }
        />

        {recurringAmount !== undefined &&
          recurringAmount !== Math.max(totalOnce, 0) && (
            <PriceRow
              withBackdrop
              variant="strong"
              label={
                <>
                  <span>
                    {t(
                      "portal:cockpit.managementTile.watchSim.checkout.thereafter",
                    )}
                  </span>
                  {recurringDate && (
                    <>
                      <br />
                      <span className="text-body-xs font-regular leading-md">
                        {recurringDate}
                      </span>
                    </>
                  )}
                </>
              }
              value={
                <>
                  <span>{getCurrency(recurringAmount, currency)}</span>
                  <span className="text-body-xs font-regular leading-md">
                    {recurringBillingPeriod ?? billingPeriod}
                  </span>
                </>
              }
            />
          )}
      </div>
    </ProfileCard>
  );
};
